import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import {
  autoEnrollAdminDriverCampaign,
  createAdminDriverCampaign,
  fetchAdminDriverCampaignDeliveries,
  fetchAdminDriverCampaignEnrollments,
  fetchAdminDriverCampaignLedger,
  fetchAdminDriverCampaigns,
  fetchAdminDriverCampaignSummary,
  fetchAdminDrivers,
  markAdminDriverCampaignPaid,
  removeAdminDriverCampaignEnrollment,
  updateAdminDriverCampaign,
  updateAdminDriverCampaignStatus,
} from '../services/api/admin';
import type {
  AdminDriverCampaign,
  AdminDriverCampaignDelivery,
  AdminDriverCampaignEnrollment,
  AdminDriverCampaignLedgerTransaction,
  AdminDriverCampaignPayload,
  AdminDriverCampaignSummary,
  AdminDriverListItem,
} from '../types/admin';
import { formatCurrency, formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const EMPTY_CAMPAIGN_FORM = {
  code: '',
  name: '',
  description: '',
  campaignType: 'DECLARED_LOAD_FOUNDING_INCENTIVE',
  status: 'DRAFT',
  startsAt: '',
  endsAt: '',
  inclusiveEndDate: true,
  currency: 'ZAR',
  budgetAmount: '',
  maxParticipants: '',
  maxQualifyingBookingsPerDriver: '20',
  rewardPerQualifyingBooking: '50',
  maxRewardPerDriver: '1000',
  minQualifyingDistanceKm: '',
  homeBaseTown: '',
  homeBaseProvince: '',
  homeBaseCountry: '',
  enrollmentPriority: '100',
  replacementSlotsAllowedDuringCampaign: true,
  notes: '',
  metadataJson: '',
};

const DRIVER_PAGE_SIZE = 10;

const DEFAULT_ENROLLMENT_FORM = {
  province: '',
  town: '',
  country: '',
  query: '',
  accountType: '',
  onboardingsStep: 'COMPLETED',
  onboardingReviewStatus: 'APPROVED',
  enabled: 'true',
  maxDrivers: '50',
  reason: '',
};

function numberInputValue(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

function numberOrUndefined(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toInstant(value: string): string {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
}

function toDateTimeLocal(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const offsetMs = parsed.getTimezoneOffset() * 60_000;
  return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
}

function normalizeLabel(value: string | null | undefined): string {
  return (value || 'UNKNOWN')
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusPillClass(status: string | null | undefined): string {
  if (['ACTIVE', 'APPROVED', 'QUALIFIED', 'POSTED', 'COMPLETED'].includes(status || '')) {
    return 'admin-pill admin-pill-success';
  }
  if (['DRAFT', 'RESERVED'].includes(status || '')) return 'admin-pill admin-pill-active';
  if (['PAUSED', 'SUSPENDED'].includes(status || '')) return 'admin-pill admin-pill-warning';
  if (['CANCELLED', 'REMOVED', 'REJECTED', 'VOIDED'].includes(status || '')) return 'admin-pill admin-pill-danger';
  return 'admin-pill';
}

function campaignToForm(campaign: AdminDriverCampaign) {
  return {
    code: campaign.code,
    name: campaign.name,
    description: campaign.description || '',
    campaignType: campaign.campaignType || 'DECLARED_LOAD_FOUNDING_INCENTIVE',
    status: campaign.status || 'DRAFT',
    startsAt: toDateTimeLocal(campaign.startsAt),
    endsAt: toDateTimeLocal(campaign.endsAt),
    inclusiveEndDate: campaign.inclusiveEndDate,
    currency: campaign.currency || 'ZAR',
    budgetAmount: numberInputValue(campaign.budgetAmount),
    maxParticipants: numberInputValue(campaign.maxParticipants),
    maxQualifyingBookingsPerDriver: numberInputValue(campaign.maxQualifyingBookingsPerDriver),
    rewardPerQualifyingBooking: numberInputValue(campaign.rewardPerQualifyingBooking),
    maxRewardPerDriver: numberInputValue(campaign.maxRewardPerDriver),
    minQualifyingDistanceKm: numberInputValue(campaign.minQualifyingDistanceKm),
    homeBaseTown: campaign.homeBaseTown || '',
    homeBaseProvince: campaign.homeBaseProvince || '',
    homeBaseCountry: campaign.homeBaseCountry || '',
    enrollmentPriority: numberInputValue(campaign.enrollmentPriority),
    replacementSlotsAllowedDuringCampaign: campaign.replacementSlotsAllowedDuringCampaign,
    notes: campaign.notes || '',
    metadataJson: campaign.metadataJson || '',
  };
}

function campaignPayload(form: typeof EMPTY_CAMPAIGN_FORM): AdminDriverCampaignPayload {
  return {
    code: form.code.trim(),
    name: form.name.trim(),
    description: form.description.trim() || null,
    campaignType: form.campaignType,
    status: form.status,
    startsAt: toInstant(form.startsAt),
    endsAt: toInstant(form.endsAt),
    inclusiveEndDate: form.inclusiveEndDate,
    currency: form.currency.trim() || 'ZAR',
    budgetAmount: numberOrUndefined(form.budgetAmount),
    maxParticipants: numberOrUndefined(form.maxParticipants),
    maxQualifyingBookingsPerDriver: numberOrUndefined(form.maxQualifyingBookingsPerDriver),
    rewardPerQualifyingBooking: numberOrUndefined(form.rewardPerQualifyingBooking),
    maxRewardPerDriver: numberOrUndefined(form.maxRewardPerDriver),
    minQualifyingDistanceKm: numberOrUndefined(form.minQualifyingDistanceKm),
    homeBaseTown: form.homeBaseTown.trim() || null,
    homeBaseProvince: form.homeBaseProvince.trim() || null,
    homeBaseCountry: form.homeBaseCountry.trim() || null,
    enrollmentPriority: numberOrUndefined(form.enrollmentPriority),
    replacementSlotsAllowedDuringCampaign: form.replacementSlotsAllowedDuringCampaign,
    notes: form.notes.trim() || null,
    metadataJson: form.metadataJson.trim() || null,
  };
}

function campaignDefaults(type: string) {
  if (type === 'DECLARED_LOAD_COMMISSION_FREE_TRIAL') {
    return {
      maxQualifyingBookingsPerDriver: '15',
      rewardPerQualifyingBooking: '0',
      maxRewardPerDriver: '0',
    };
  }
  return {
    maxQualifyingBookingsPerDriver: '20',
    rewardPerQualifyingBooking: '50',
    maxRewardPerDriver: '1000',
  };
}

function enrollmentResultMessage(enrolledDrivers: number, skippedDrivers: number, skipped: string[]): string {
  const base = `${enrolledDrivers} enrolled, ${skippedDrivers} skipped.`;
  if (!skipped.length) return base;
  return `${base} ${skipped.slice(0, 3).join(' | ')}`;
}

const AdminDriverCampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<AdminDriverCampaign[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [enrollments, setEnrollments] = useState<AdminDriverCampaignEnrollment[]>([]);
  const [summary, setSummary] = useState<AdminDriverCampaignSummary | null>(null);
  const [deliveries, setDeliveries] = useState<AdminDriverCampaignDelivery[]>([]);
  const [ledger, setLedger] = useState<AdminDriverCampaignLedgerTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState('');
  const [campaignForm, setCampaignForm] = useState(EMPTY_CAMPAIGN_FORM);
  const [drivers, setDrivers] = useState<AdminDriverListItem[]>([]);
  const [selectedDriverIds, setSelectedDriverIds] = useState<string[]>([]);
  const [optimisticEnrolledDriverIds, setOptimisticEnrolledDriverIds] = useState<string[]>([]);
  const [driverPage, setDriverPage] = useState(0);
  const [driverTotalElements, setDriverTotalElements] = useState(0);
  const [driverTotalPages, setDriverTotalPages] = useState(0);
  const [driversLoading, setDriversLoading] = useState(false);
  const [enrollmentForm, setEnrollmentForm] = useState(DEFAULT_ENROLLMENT_FORM);
  const [paymentForm, setPaymentForm] = useState({ amount: '', reference: '', reason: '' });

  const selectedCampaign = useMemo(
    () => campaigns.find((campaign) => campaign.campaignId === selectedCampaignId) || null,
    [campaigns, selectedCampaignId],
  );

  const enrolledDriverIds = useMemo(
    () => new Set([
      ...enrollments.filter((enrollment) => enrollment.status !== 'REMOVED').map((enrollment) => enrollment.driverId),
      ...optimisticEnrolledDriverIds,
    ]),
    [enrollments, optimisticEnrolledDriverIds],
  );

  const selectableDrivers = useMemo(
    () => drivers.filter((driver) => !enrolledDriverIds.has(driver.driverId)),
    [drivers, enrolledDriverIds],
  );

  const selectedDriverIdSet = useMemo(() => new Set(selectedDriverIds), [selectedDriverIds]);

  const currentPageSelected = useMemo(
    () => selectableDrivers.length > 0 && selectableDrivers.every((driver) => selectedDriverIdSet.has(driver.driverId)),
    [selectableDrivers, selectedDriverIdSet],
  );

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchAdminDriverCampaigns({ page: 0, size: 50, sort: 'createdAt,desc' });
      setCampaigns(response.items);
      setSelectedCampaignId((current) => current || response.items[0]?.campaignId || '');
    } catch (loadError: any) {
      setError(loadError.message || 'Could not load driver campaigns.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCampaignDetail = useCallback(async () => {
    if (!selectedCampaignId) return;
    setDetailLoading(true);
    setError('');
    try {
      const [enrollmentPage, ledgerPage] = await Promise.all([
        fetchAdminDriverCampaignEnrollments(selectedCampaignId, { page: 0, size: 50, sort: 'createdAt,desc' }),
        fetchAdminDriverCampaignLedger(selectedCampaignId, {
          page: 0,
          size: 50,
          sort: 'eventAt,desc',
        }),
      ]);
      setEnrollments(enrollmentPage.items);
      setLedger(ledgerPage.items);
    } catch (loadError: any) {
      setError(loadError.message || 'Could not load campaign details.');
    } finally {
      setDetailLoading(false);
    }
  }, [selectedCampaignId]);

  const loadDriverSummary = useCallback(async (driverId: string) => {
    if (!selectedCampaignId || !driverId) return;
    setDetailLoading(true);
    setError('');
    setSummary(null);
    setDeliveries([]);
    try {
      const [summaryResponse, deliveriesPage, ledgerPage] = await Promise.all([
        fetchAdminDriverCampaignSummary(selectedCampaignId, driverId),
        fetchAdminDriverCampaignDeliveries(selectedCampaignId, driverId, { page: 0, size: 50, sort: 'createdAt,desc' }),
        fetchAdminDriverCampaignLedger(selectedCampaignId, {
          driverId,
          page: 0,
          size: 50,
          sort: 'eventAt,desc',
        }),
      ]);
      setSummary(summaryResponse);
      setDeliveries(deliveriesPage.items);
      setLedger(ledgerPage.items);
    } catch (loadError: any) {
      setError(loadError.message || 'Could not load the driver campaign summary.');
    } finally {
      setDetailLoading(false);
    }
  }, [selectedCampaignId]);

  const loadDrivers = useCallback(async () => {
    setDriversLoading(true);
    setError('');
    try {
      const response = await fetchAdminDrivers({
        ...(enrollmentForm.query.trim() ? { query: enrollmentForm.query.trim() } : {}),
        ...(enrollmentForm.province.trim() ? { province: enrollmentForm.province.trim() } : {}),
        ...(enrollmentForm.town.trim() ? { town: enrollmentForm.town.trim() } : {}),
        ...(enrollmentForm.country.trim() ? { country: enrollmentForm.country.trim() } : {}),
        ...(enrollmentForm.accountType ? { accountType: enrollmentForm.accountType } : {}),
        onboardingsStep: enrollmentForm.onboardingsStep,
        ...(enrollmentForm.onboardingReviewStatus ? { onboardingReviewStatus: enrollmentForm.onboardingReviewStatus } : {}),
        ...(enrollmentForm.enabled ? { enabled: enrollmentForm.enabled === 'true' } : {}),
        page: driverPage,
        size: DRIVER_PAGE_SIZE,
      });
      setDrivers(response.items);
      setDriverTotalElements(response.totalElements);
      setDriverTotalPages(response.totalPages);
      setSelectedDriverIds((current) => current.filter((driverId) => response.items.some((driver) => driver.driverId === driverId)));
    } catch (loadError: any) {
      setError(loadError.message || 'Could not load drivers.');
    } finally {
      setDriversLoading(false);
    }
  }, [
    driverPage,
    enrollmentForm.accountType,
    enrollmentForm.enabled,
    enrollmentForm.onboardingsStep,
    enrollmentForm.onboardingReviewStatus,
    enrollmentForm.province,
    enrollmentForm.query,
    enrollmentForm.town,
    enrollmentForm.country,
  ]);

  useEffect(() => {
    void loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    void loadCampaignDetail();
  }, [loadCampaignDetail]);

  useEffect(() => {
    void loadDrivers();
  }, [loadDrivers]);

  useEffect(() => {
    setSelectedDriverId('');
    setSelectedDriverIds([]);
    setOptimisticEnrolledDriverIds([]);
    setSummary(null);
    setDeliveries([]);
    setSummaryModalOpen(false);
  }, [selectedCampaignId]);

  const updateCampaignForm = (key: keyof typeof EMPTY_CAMPAIGN_FORM, value: string | boolean) => {
    setCampaignForm((current) => ({ ...current, [key]: value }));
  };

  const resetCampaignForm = () => {
    setEditingCampaignId('');
    setCampaignForm(EMPTY_CAMPAIGN_FORM);
  };

  const updateEnrollmentForm = (key: keyof typeof enrollmentForm, value: string | boolean) => {
    setDriverPage(0);
    setSelectedDriverIds([]);
    setEnrollmentForm((current) => ({ ...current, [key]: value }));
  };

  const clearEnrollmentFilters = () => {
    setDriverPage(0);
    setSelectedDriverIds([]);
    setEnrollmentForm(DEFAULT_ENROLLMENT_FORM);
  };

  const openNewCampaignModal = () => {
    resetCampaignForm();
    setCampaignModalOpen(true);
  };

  const closeCampaignModal = () => {
    resetCampaignForm();
    setCampaignModalOpen(false);
  };

  const handleCampaignTypeChange = (value: string) => {
    setCampaignForm((current) => ({ ...current, campaignType: value, ...campaignDefaults(value) }));
  };

  const submitCampaign = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const payload = campaignPayload(campaignForm);
      const saved = editingCampaignId
        ? await updateAdminDriverCampaign(editingCampaignId, payload)
        : await createAdminDriverCampaign(payload);
      setMessage(editingCampaignId ? 'Campaign updated.' : 'Campaign created.');
      setSelectedCampaignId(saved.campaignId);
      resetCampaignForm();
      setCampaignModalOpen(false);
      await loadCampaigns();
    } catch (saveError: any) {
      setError(saveError.message || 'Could not save the campaign.');
    } finally {
      setSaving(false);
    }
  };

  const editCampaign = (campaign: AdminDriverCampaign) => {
    setEditingCampaignId(campaign.campaignId);
    setCampaignForm(campaignToForm(campaign));
    setSelectedCampaignId(campaign.campaignId);
    setCampaignModalOpen(true);
  };

  const changeCampaignStatus = async (status: string) => {
    if (!selectedCampaignId) return;
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await updateAdminDriverCampaignStatus(selectedCampaignId, {
        status,
        reason: `Admin set campaign to ${status}`,
        idempotencyKey: `campaign-status:${selectedCampaignId}:${status}:${Date.now()}`,
      });
      setMessage(`Campaign set to ${normalizeLabel(status)}.`);
      await loadCampaigns();
    } catch (statusError: any) {
      setError(statusError.message || 'Could not update campaign status.');
    } finally {
      setSaving(false);
    }
  };

  const submitEnrollment = async (event?: React.FormEvent | React.MouseEvent) => {
    event?.preventDefault();
    if (!selectedCampaignId) return;
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const response = await autoEnrollAdminDriverCampaign({
        campaignId: selectedCampaignId,
        province: enrollmentForm.province.trim() || undefined,
        town: enrollmentForm.town.trim() || undefined,
        country: enrollmentForm.country.trim() || undefined,
        query: enrollmentForm.query.trim() || undefined,
        accountType: enrollmentForm.accountType || undefined,
        onboardingsStep: enrollmentForm.onboardingsStep,
        onboardingReviewStatus: enrollmentForm.onboardingReviewStatus || undefined,
        enabled: enrollmentForm.enabled ? enrollmentForm.enabled === 'true' : undefined,
        allMatchingDrivers: true,
        maxDrivers: numberOrUndefined(enrollmentForm.maxDrivers),
        reason: enrollmentForm.reason.trim() || undefined,
        idempotencyKey: `campaign-enroll:${selectedCampaignId}:${Date.now()}`,
      });
      setMessage(enrollmentResultMessage(response.enrolledDrivers, response.skippedDrivers, response.skipped));
      if (response.enrolledDrivers > 0) {
        const enrolledIds = response.enrollments.map((enrollment) => enrollment.driverId);
        setOptimisticEnrolledDriverIds((current) => Array.from(new Set([...current, ...enrolledIds])));
      }
      setSelectedDriverIds([]);
      setEnrollmentForm((current) => ({ ...current, reason: '' }));
      await Promise.all([loadCampaigns(), loadCampaignDetail(), loadDrivers()]);
    } catch (enrollError: any) {
      setError(enrollError.message || 'Could not enroll drivers.');
    } finally {
      setSaving(false);
    }
  };

  const enrollSelectedDrivers = async () => {
    if (!selectedCampaignId || !selectedDriverIds.length) return;
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const response = await autoEnrollAdminDriverCampaign({
        campaignId: selectedCampaignId,
        driverIds: selectedDriverIds,
        reason: enrollmentForm.reason.trim() || undefined,
        idempotencyKey: `campaign-enroll-selected:${selectedCampaignId}:${Date.now()}`,
      });
      setMessage(enrollmentResultMessage(response.enrolledDrivers, response.skippedDrivers, response.skipped));
      if (response.enrolledDrivers > 0) {
        const enrolledIds = response.enrollments.map((enrollment) => enrollment.driverId);
        setOptimisticEnrolledDriverIds((current) => Array.from(new Set([...current, ...enrolledIds])));
      }
      setSelectedDriverIds([]);
      await Promise.all([loadCampaigns(), loadCampaignDetail(), loadDrivers()]);
    } catch (enrollError: any) {
      setError(enrollError.message || 'Could not enroll selected drivers.');
    } finally {
      setSaving(false);
    }
  };

  const toggleDriverSelection = (driverId: string) => {
    setSelectedDriverIds((current) => (
      current.includes(driverId)
        ? current.filter((selectedId) => selectedId !== driverId)
        : [...current, driverId]
    ));
  };

  const toggleCurrentPageSelection = () => {
    setSelectedDriverIds((current) => {
      const pageIds = selectableDrivers.map((driver) => driver.driverId);
      if (pageIds.every((driverId) => current.includes(driverId))) {
        return current.filter((driverId) => !pageIds.includes(driverId));
      }
      return Array.from(new Set([...current, ...pageIds]));
    });
  };

  const enrollDriver = async (driver: AdminDriverListItem) => {
    if (!selectedCampaignId || enrolledDriverIds.has(driver.driverId)) return;
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const response = await autoEnrollAdminDriverCampaign({
        campaignId: selectedCampaignId,
        driverId: driver.driverId,
        reason: enrollmentForm.reason.trim() || undefined,
        idempotencyKey: `campaign-enroll:${selectedCampaignId}:${driver.driverId}:${Date.now()}`,
      });
      setMessage(enrollmentResultMessage(response.enrolledDrivers, response.skippedDrivers, response.skipped));
      if (response.enrolledDrivers > 0) {
        setOptimisticEnrolledDriverIds((current) => Array.from(new Set([...current, driver.driverId])));
      }
      await Promise.all([loadCampaigns(), loadCampaignDetail(), loadDrivers()]);
    } catch (enrollError: any) {
      setError(enrollError.message || 'Could not enroll this driver.');
    } finally {
      setSaving(false);
    }
  };

  const selectDriver = (driverId: string) => {
    setSelectedDriverId(driverId);
    setPaymentForm({ amount: '', reference: '', reason: '' });
    setSummaryModalOpen(true);
    void loadDriverSummary(driverId);
  };

  const submitPayment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedCampaignId || !selectedDriverId) return;
    const amount = Number(paymentForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Enter a payout amount greater than zero.');
      return;
    }
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await markAdminDriverCampaignPaid(selectedCampaignId, selectedDriverId, {
        amount,
        currency: summary?.currency || selectedCampaign?.currency || 'ZAR',
        reference: paymentForm.reference.trim() || undefined,
        reason: paymentForm.reason.trim() || undefined,
        paidAt: new Date().toISOString(),
        idempotencyKey: `campaign-payment:${selectedCampaignId}:${selectedDriverId}:${Date.now()}`,
      });
      setMessage('Driver payment recorded.');
      setPaymentForm({ amount: '', reference: '', reason: '' });
      await Promise.all([loadCampaignDetail(), loadDriverSummary(selectedDriverId)]);
    } catch (paymentError: any) {
      setError(paymentError.message || 'Could not record the driver payment.');
    } finally {
      setSaving(false);
    }
  };

  const removeEnrollment = async (enrollment: AdminDriverCampaignEnrollment) => {
    if (!selectedCampaignId || enrollment.status === 'REMOVED') return;
    const confirmed = window.confirm(`Remove ${enrollment.driverName || enrollment.driverId} from this campaign?`);
    if (!confirmed) return;

    setSaving(true);
    setMessage('');
    setError('');
    try {
      await removeAdminDriverCampaignEnrollment(selectedCampaignId, enrollment.driverId, {
        reason: 'Removed by admin from campaign console',
        idempotencyKey: `campaign-remove:${selectedCampaignId}:${enrollment.driverId}:${Date.now()}`,
      });
      setMessage('Driver removed from campaign.');
      setOptimisticEnrolledDriverIds((current) => current.filter((driverId) => driverId !== enrollment.driverId));
      if (selectedDriverId === enrollment.driverId) {
        setSelectedDriverId('');
        setSummaryModalOpen(false);
        setSummary(null);
        setDeliveries([]);
      }
      await Promise.all([loadCampaigns(), loadCampaignDetail(), loadDrivers()]);
    } catch (removeError: any) {
      setError(removeError.message || 'Could not remove driver from campaign.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell
      badge="Driver Campaigns"
      copy="Create campaign windows, enroll drivers, audit qualifying declared loads, and record incentive payments."
      title="Campaign management"
    >
      {error ? <div className="register-inline-error">{error}</div> : null}
      {message ? <div className="register-inline-success">{message}</div> : null}

      <section className="admin-panel">
          <div className="admin-panel-header">
            <div>
              <h2 className="admin-panel-title">Campaigns</h2>
              <p className="admin-panel-copy">Select a campaign to manage enrollments and payouts.</p>
            </div>
            <div className="admin-inline-actions">
              <button className="register-submit register-submit-inline" onClick={openNewCampaignModal} type="button">
                New campaign
              </button>
              <button className="register-secondary-button" disabled={loading} onClick={() => void loadCampaigns()} type="button">
                Refresh
              </button>
            </div>
          </div>

          {loading ? (
            <div className="register-loader" />
          ) : (
            <div className="admin-table">
              <div className="admin-table-head admin-table-head-campaigns">
                <span>Campaign</span>
                <span>Window</span>
                <span>Slots</span>
                <span>Reward</span>
                <span>Actions</span>
              </div>
              {campaigns.map((campaign) => (
                <button
                  className={`admin-table-row admin-table-row-campaigns admin-table-row-button${selectedCampaignId === campaign.campaignId ? ' admin-table-row-selected' : ''}`}
                  key={campaign.campaignId}
                  onClick={() => {
                    setSelectedCampaignId(campaign.campaignId);
                  }}
                  type="button"
                >
                  <span>
                    <strong>{campaign.name}</strong>
                    <small>{campaign.code}</small>
                    <span className={statusPillClass(campaign.status)}>{normalizeLabel(campaign.status)}</span>
                  </span>
                  <span>
                    <strong>{formatDateTime(campaign.startsAt)}</strong>
                    <small>{formatDateTime(campaign.endsAt)}</small>
                  </span>
                  <span>
                    <strong>{campaign.approvedParticipants} approved</strong>
                    <small>{campaign.consumedSlots} consumed</small>
                    <small>{campaign.remainingSlots ?? 'Unlimited'} remaining</small>
                  </span>
                  <span>
                    <strong>{formatCurrency(campaign.rewardPerQualifyingBooking)}</strong>
                    <small>Max {formatCurrency(campaign.maxRewardPerDriver)}</small>
                    <small>{campaign.maxQualifyingBookingsPerDriver ?? 0} loads</small>
                  </span>
                  <span className="admin-inline-actions">
                    <span className="register-secondary-button admin-inline-button" onClick={(event) => { event.stopPropagation(); editCampaign(campaign); }}>
                      Edit
                    </span>
                  </span>
                </button>
              ))}
              {!campaigns.length ? <p className="admin-empty">No campaigns have been created yet.</p> : null}
            </div>
          )}
      </section>

      <section className="admin-panel admin-campaign-enrollment-panel">
          <div className="admin-panel-header">
            <div>
              <h2 className="admin-panel-title">Enrollment</h2>
              <p className="admin-panel-copy">{selectedCampaign ? selectedCampaign.name : 'Select a campaign first.'}</p>
            </div>
            {selectedCampaign ? (
              <div className="admin-inline-actions">
                <button className="register-secondary-button" disabled={saving} onClick={() => void changeCampaignStatus('ACTIVE')} type="button">Activate</button>
                <button className="register-secondary-button" disabled={saving} onClick={() => void changeCampaignStatus('PAUSED')} type="button">Pause</button>
                <button className="register-secondary-button" disabled={saving} onClick={() => void changeCampaignStatus('COMPLETED')} type="button">Complete</button>
              </div>
            ) : null}
          </div>

          <form className="admin-campaign-form admin-campaign-driver-filters" onSubmit={submitEnrollment}>
            <label className="admin-filter-field">
              <span>Province</span>
              <input className="register-input" onChange={(event) => updateEnrollmentForm('province', event.target.value)} placeholder="Gauteng" value={enrollmentForm.province} />
            </label>
            <label className="admin-filter-field">
              <span>Town</span>
              <input className="register-input" onChange={(event) => updateEnrollmentForm('town', event.target.value)} placeholder="Johannesburg" value={enrollmentForm.town} />
            </label>
            <label className="admin-filter-field">
              <span>Country</span>
              <input className="register-input" onChange={(event) => updateEnrollmentForm('country', event.target.value)} value={enrollmentForm.country} />
            </label>
            <label className="admin-filter-field">
              <span>Driver search</span>
              <input className="register-input" onChange={(event) => updateEnrollmentForm('query', event.target.value)} placeholder="Name, phone, email" value={enrollmentForm.query} />
            </label>
            <label className="admin-filter-field">
              <span>Account type</span>
              <select className="register-input" onChange={(event) => updateEnrollmentForm('accountType', event.target.value)} value={enrollmentForm.accountType}>
                <option value="">Any type</option>
                <option value="INDIVIDUAL_OWNER">Individual owner</option>
                <option value="COMPANY_DRIVER">Company driver</option>
              </select>
            </label>
            <label className="admin-filter-field">
              <span>Onboarding</span>
              <select className="register-input" disabled value={enrollmentForm.onboardingsStep}>
                <option value="COMPLETED">Completed</option>
              </select>
            </label>
            <label className="admin-filter-field">
              <span>Review</span>
              <select className="register-input" disabled value={enrollmentForm.onboardingReviewStatus}>
                <option value="APPROVED">Approved</option>
              </select>
            </label>
            <label className="admin-filter-field">
              <span>Enabled</span>
              <select className="register-input" onChange={(event) => updateEnrollmentForm('enabled', event.target.value)} value={enrollmentForm.enabled}>
                <option value="">Any</option>
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </label>
            <label className="admin-filter-field">
              <span>Max matching drivers</span>
              <input className="register-input" max="500" min="1" onChange={(event) => updateEnrollmentForm('maxDrivers', event.target.value)} type="number" value={enrollmentForm.maxDrivers} />
            </label>
            <label className="admin-filter-field admin-toolbar-wide">
              <span>Reason</span>
              <input className="register-input" onChange={(event) => updateEnrollmentForm('reason', event.target.value)} placeholder="Optional admin note for this enrollment" value={enrollmentForm.reason} />
            </label>
          </form>

          <div className="admin-campaign-selection-bar">
            <span>{selectedDriverIds.length} selected</span>
            <div className="admin-inline-actions">
              <button className="register-submit register-submit-inline" disabled={saving || !selectedCampaignId || !selectedDriverIds.length} onClick={() => void enrollSelectedDrivers()} type="button">
                {saving ? 'Enrolling...' : 'Enroll selected'}
              </button>
              <button className="register-secondary-button" disabled={saving || !selectedCampaignId} onClick={submitEnrollment} type="button">
                Enroll all matching filters
              </button>
              <button className="register-secondary-button" onClick={clearEnrollmentFilters} type="button">
                Clear filters
              </button>
            </div>
          </div>

          <div className="admin-campaign-section-title">Matching drivers</div>
          <div className="admin-table admin-campaign-driver-table">
            <div className="admin-table-head admin-table-head-campaign-drivers">
              <span>
                <input checked={currentPageSelected} disabled={!selectableDrivers.length} onChange={toggleCurrentPageSelection} type="checkbox" />
              </span>
              <span>Driver</span>
              <span>Base</span>
              <span>State</span>
              <span>Action</span>
            </div>
            {driversLoading ? <div className="register-loader" /> : null}
            {drivers.map((driver) => {
              const alreadyEnrolled = enrolledDriverIds.has(driver.driverId);
              return (
                <div className="admin-table-row admin-table-row-campaign-drivers" key={driver.driverId}>
                  <span>
                    <input
                      checked={selectedDriverIdSet.has(driver.driverId)}
                      disabled={alreadyEnrolled}
                      onChange={() => toggleDriverSelection(driver.driverId)}
                      type="checkbox"
                    />
                  </span>
                  <span>
                    <strong>{driver.name || driver.driverId}</strong>
                    <small>{driver.email || 'Email unavailable'}</small>
                    <small>{driver.phoneE164 || 'Phone unavailable'}</small>
                  </span>
                  <span>
                    <strong>{driver.homeBaseProvince || 'Province unavailable'}</strong>
                    <small>{driver.homeBaseTown || 'Town unavailable'}</small>
                    <small>{driver.homeBaseCountry || 'Country unavailable'}</small>
                  </span>
                  <span>
                    <span className={statusPillClass(driver.onboardingReviewStatus)}>{normalizeLabel(driver.onboardingReviewStatus)}</span>
                    <small>{normalizeLabel(driver.onboardingsStep)}</small>
                    <small>{normalizeLabel(driver.accountType)}</small>
                    <small>{driver.enabled ? 'Enabled' : 'Disabled'}</small>
                  </span>
                  <span className="admin-inline-actions">
                    <button
                      className="register-secondary-button admin-inline-button"
                      disabled={saving || !selectedCampaignId || alreadyEnrolled}
                      onClick={() => void enrollDriver(driver)}
                      type="button"
                    >
                      {alreadyEnrolled ? 'Enrolled' : 'Enroll'}
                    </button>
                  </span>
                </div>
              );
            })}
            {!driversLoading && !drivers.length ? <p className="admin-empty">No drivers matched these filters.</p> : null}
          </div>
          <div className="admin-pagination">
            <button className="register-secondary-button" disabled={driversLoading || driverPage <= 0} onClick={() => setDriverPage((current) => Math.max(0, current - 1))} type="button">
              Previous
            </button>
            <span>
              Page {driverTotalPages ? driverPage + 1 : 0} of {driverTotalPages || 0} ({driverTotalElements} drivers)
            </span>
            <button className="register-secondary-button" disabled={driversLoading || driverPage + 1 >= driverTotalPages} onClick={() => setDriverPage((current) => current + 1)} type="button">
              Next
            </button>
          </div>

          <div className="admin-campaign-section-title">Enrolled drivers</div>
          <div className="admin-table admin-campaign-enrollment-table">
            {detailLoading ? <div className="register-loader" /> : null}
            {enrollments.map((enrollment) => (
              <button
                className={`admin-table-row admin-table-row-button admin-table-row-campaign-enrollments${selectedDriverId === enrollment.driverId ? ' admin-table-row-selected' : ''}`}
                key={enrollment.enrollmentId}
                onClick={() => selectDriver(enrollment.driverId)}
                type="button"
              >
                <span>
                  <strong>{enrollment.driverName || enrollment.driverId}</strong>
                  <small>{enrollment.driverPhone || 'Phone unavailable'}</small>
                  <small>{enrollment.driverEmail || 'Email unavailable'}</small>
                </span>
                <span>
                  <span className={statusPillClass(enrollment.status)}>{normalizeLabel(enrollment.status)}</span>
                  <small>{enrollment.homeBaseProvince || 'Province unavailable'}</small>
                  <small>{enrollment.homeBaseTown || 'Town unavailable'}</small>
                </span>
                <span>
                  <strong>{enrollment.slotConsumed ? 'Slot consumed' : 'Slot open'}</strong>
                  <small>{formatDateTime(enrollment.enrolledAt)}</small>
                </span>
                <span className="admin-inline-actions">
                  <span
                    className="register-secondary-button admin-inline-button"
                    onClick={(event) => {
                      event.stopPropagation();
                      selectDriver(enrollment.driverId);
                    }}
                  >
                    View summary
                  </span>
                  {enrollment.status !== 'REMOVED' ? (
                    <span
                      className="register-secondary-button admin-inline-button admin-danger-inline-button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void removeEnrollment(enrollment);
                      }}
                    >
                      Remove
                    </span>
                  ) : (
                    <small>Removed {formatDateTime(enrollment.removedAt)}</small>
                  )}
                </span>
              </button>
            ))}
            {!detailLoading && !enrollments.length ? <p className="admin-empty">No drivers enrolled in this campaign yet.</p> : null}
          </div>
      </section>

      {summaryModalOpen ? (
        <div className="admin-modal-backdrop">
          <div className="admin-modal admin-campaign-summary-modal" role="dialog" aria-modal="true" aria-labelledby="campaign-summary-modal-title">
            <div className="admin-modal-header">
              <div>
                <h2 id="campaign-summary-modal-title">Driver summary</h2>
                <p>{selectedDriverId || 'Select an enrolled driver.'}</p>
              </div>
              <button aria-label="Close driver summary" className="admin-modal-close" onClick={() => setSummaryModalOpen(false)} type="button">
                x
              </button>
            </div>

            {summary ? (
              <>
                <div className="admin-grid admin-grid-cards admin-campaign-summary-grid">
                  <div className="admin-stat-card">
                    <span className="admin-stat-label">Earned</span>
                    <strong>{formatCurrency(summary.earnedAmount)}</strong>
                    <span className="admin-stat-note">{summary.qualifiedDeliveries} qualified deliveries.</span>
                  </div>
                  <div className="admin-stat-card">
                    <span className="admin-stat-label">Paid</span>
                    <strong>{formatCurrency(summary.paidAmount)}</strong>
                    <span className="admin-stat-note">{formatCurrency(summary.outstandingAmount)} outstanding.</span>
                  </div>
                  <div className="admin-stat-card">
                    <span className="admin-stat-label">Usage</span>
                    <strong>{summary.reservedDeliveries}/{summary.maxQualifyingDeliveries}</strong>
                    <span className="admin-stat-note">{summary.remainingDeliveries} remaining.</span>
                  </div>
                </div>

                <form className="admin-campaign-payment-form" onSubmit={submitPayment}>
                  <label className="admin-filter-field">
                    <span>Amount</span>
                    <input className="register-input" max={summary.outstandingAmount || undefined} min="0.01" onChange={(event) => setPaymentForm((current) => ({ ...current, amount: event.target.value }))} step="0.01" type="number" value={paymentForm.amount} />
                  </label>
                  <label className="admin-filter-field">
                    <span>Reference</span>
                    <input className="register-input" onChange={(event) => setPaymentForm((current) => ({ ...current, reference: event.target.value }))} placeholder="EFT reference" value={paymentForm.reference} />
                  </label>
                  <label className="admin-filter-field admin-toolbar-wide">
                    <span>Reason</span>
                    <input className="register-input" onChange={(event) => setPaymentForm((current) => ({ ...current, reason: event.target.value }))} placeholder="Payment batch or admin note" value={paymentForm.reason} />
                  </label>
                  <button className="register-submit register-submit-inline" disabled={saving || summary.outstandingAmount <= 0} type="submit">
                    Mark paid
                  </button>
                </form>
              </>
            ) : (
              <p className="admin-empty">No campaign summary loaded.</p>
            )}

            <div className="admin-campaign-section-title">Qualifying deliveries</div>
            <div className="admin-table">
              <div className="admin-table-head admin-table-head-campaign-deliveries">
                <span>Booking</span>
                <span>Status</span>
                <span>Distance</span>
                <span>Earned</span>
              </div>
              {deliveries.map((delivery) => (
                <div className="admin-table-row admin-table-row-campaign-deliveries" key={delivery.usageId}>
                  <span>
                    {delivery.tripId ? (
                      <Link className="admin-row-link" to={`/admin/trips/${delivery.tripId}`}>
                        {delivery.bookingId || delivery.tripId}
                      </Link>
                    ) : (
                      <strong>{delivery.bookingId || 'Booking pending'}</strong>
                    )}
                    <small>{delivery.paymentWaived ? 'Campaign payment waived' : 'Payment not waived'}</small>
                  </span>
                  <span>
                    <span className={statusPillClass(delivery.status)}>{normalizeLabel(delivery.status)}</span>
                    <small>{formatDateTime(delivery.qualifiedAt || delivery.rejectedAt || delivery.paymentWaivedAt)}</small>
                  </span>
                  <span>
                    <strong>{delivery.qualifyingDistanceKm ?? 0} km</strong>
                    <small>{delivery.usageType || 'Campaign usage'}</small>
                  </span>
                  <span>
                    <strong>{formatCurrency(delivery.earnedAmount)}</strong>
                    {delivery.rejectionReason ? <small>{delivery.rejectionReason}</small> : null}
                  </span>
                </div>
              ))}
              {!deliveries.length ? <p className="admin-empty">No campaign deliveries recorded for this driver.</p> : null}
            </div>

            <div className="admin-campaign-section-title">Ledger</div>
            <div className="admin-table">
              <div className="admin-table-head admin-table-head-campaign-ledger">
                <span>Type</span>
                <span>Amount</span>
                <span>Reference</span>
                <span>When</span>
              </div>
              {ledger.map((entry) => (
                <div className="admin-table-row admin-table-row-campaign-ledger" key={entry.transactionId}>
                  <span>
                    <span className={statusPillClass(entry.status)}>{normalizeLabel(entry.transactionType)}</span>
                    <small>{entry.driverId}</small>
                  </span>
                  <span>
                    <strong>{formatCurrency(entry.amount)}</strong>
                    <small>{normalizeLabel(entry.status)}</small>
                  </span>
                  <span>
                    <strong>{entry.reference || 'No reference'}</strong>
                    <small>{entry.reason || 'No note'}</small>
                  </span>
                  <span>
                    <strong>{formatDateTime(entry.eventAt)}</strong>
                    <small>{entry.createdByActorEmail || 'System'}</small>
                  </span>
                </div>
              ))}
              {!ledger.length ? <p className="admin-empty">No ledger entries for this campaign selection.</p> : null}
            </div>
          </div>
        </div>
      ) : null}

      {campaignModalOpen ? (
        <div className="admin-modal-backdrop">
          <div className="admin-modal admin-campaign-modal" role="dialog" aria-modal="true" aria-labelledby="campaign-modal-title">
            <div className="admin-modal-header">
              <div>
                <h2 id="campaign-modal-title">{editingCampaignId ? 'Edit campaign' : 'New campaign'}</h2>
                <p>Configure limits, dates, rewards, and matching scope.</p>
              </div>
              <button aria-label="Close campaign form" className="admin-modal-close" onClick={closeCampaignModal} type="button">
                x
              </button>
            </div>
            <form className="admin-campaign-form" onSubmit={submitCampaign}>
              <label className="admin-filter-field admin-toolbar-wide">
                <span>Name</span>
                <input className="register-input" onChange={(event) => updateCampaignForm('name', event.target.value)} required value={campaignForm.name} />
              </label>
              <label className="admin-filter-field">
                <span>Code</span>
                <input className="register-input" onChange={(event) => updateCampaignForm('code', event.target.value)} required value={campaignForm.code} />
              </label>
              <label className="admin-filter-field">
                <span>Type</span>
                <select className="register-input" onChange={(event) => handleCampaignTypeChange(event.target.value)} value={campaignForm.campaignType}>
                  <option value="DECLARED_LOAD_FOUNDING_INCENTIVE">Incentive</option>
                  <option value="DECLARED_LOAD_COMMISSION_FREE_TRIAL">Free trial</option>
                </select>
              </label>
              <label className="admin-filter-field">
                <span>Status</span>
                <select className="register-input" onChange={(event) => updateCampaignForm('status', event.target.value)} value={campaignForm.status}>
                  <option value="DRAFT">Draft</option>
                  <option value="ACTIVE">Active</option>
                  <option value="PAUSED">Paused</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </label>
              <label className="admin-filter-field">
                <span>Starts</span>
                <input className="register-input" onChange={(event) => updateCampaignForm('startsAt', event.target.value)} required type="datetime-local" value={campaignForm.startsAt} />
              </label>
              <label className="admin-filter-field">
                <span>Ends</span>
                <input className="register-input" onChange={(event) => updateCampaignForm('endsAt', event.target.value)} required type="datetime-local" value={campaignForm.endsAt} />
              </label>
              <label className="admin-filter-field">
                <span>Max drivers</span>
                <input className="register-input" min="1" onChange={(event) => updateCampaignForm('maxParticipants', event.target.value)} type="number" value={campaignForm.maxParticipants} />
              </label>
              <label className="admin-filter-field">
                <span>Per driver loads</span>
                <input className="register-input" min="1" onChange={(event) => updateCampaignForm('maxQualifyingBookingsPerDriver', event.target.value)} type="number" value={campaignForm.maxQualifyingBookingsPerDriver} />
              </label>
              <label className="admin-filter-field">
                <span>Reward</span>
                <input className="register-input" min="0" onChange={(event) => updateCampaignForm('rewardPerQualifyingBooking', event.target.value)} step="0.01" type="number" value={campaignForm.rewardPerQualifyingBooking} />
              </label>
              <label className="admin-filter-field">
                <span>Max reward</span>
                <input className="register-input" min="0" onChange={(event) => updateCampaignForm('maxRewardPerDriver', event.target.value)} step="0.01" type="number" value={campaignForm.maxRewardPerDriver} />
              </label>
              <label className="admin-filter-field">
                <span>Budget</span>
                <input className="register-input" min="0" onChange={(event) => updateCampaignForm('budgetAmount', event.target.value)} step="0.01" type="number" value={campaignForm.budgetAmount} />
              </label>
              <label className="admin-filter-field">
                <span>Minimum distance km</span>
                <input className="register-input" min="0" onChange={(event) => updateCampaignForm('minQualifyingDistanceKm', event.target.value)} placeholder="5" step="0.01" type="number" value={campaignForm.minQualifyingDistanceKm} />
              </label>
              <label className="admin-filter-field">
                <span>Province</span>
                <input className="register-input" onChange={(event) => updateCampaignForm('homeBaseProvince', event.target.value)} placeholder="Western Cape" value={campaignForm.homeBaseProvince} />
              </label>
              <label className="admin-filter-field">
                <span>Town</span>
                <input className="register-input" onChange={(event) => updateCampaignForm('homeBaseTown', event.target.value)} placeholder="Cape Town" value={campaignForm.homeBaseTown} />
              </label>
              <label className="admin-filter-field">
                <span>Country</span>
                <input className="register-input" onChange={(event) => updateCampaignForm('homeBaseCountry', event.target.value)} placeholder="Leave blank for any country" value={campaignForm.homeBaseCountry} />
              </label>
              <label className="admin-checkbox-row admin-campaign-checkbox">
                <input checked={campaignForm.inclusiveEndDate} onChange={(event) => updateCampaignForm('inclusiveEndDate', event.target.checked)} type="checkbox" />
                Inclusive end
              </label>
              <label className="admin-checkbox-row admin-campaign-checkbox">
                <input checked={campaignForm.replacementSlotsAllowedDuringCampaign} onChange={(event) => updateCampaignForm('replacementSlotsAllowedDuringCampaign', event.target.checked)} type="checkbox" />
                Replacement slots
              </label>
              <label className="admin-filter-field admin-toolbar-wide">
                <span>Notes</span>
                <textarea className="register-input" onChange={(event) => updateCampaignForm('notes', event.target.value)} rows={3} value={campaignForm.notes} />
              </label>
              <div className="admin-modal-actions admin-toolbar-wide">
                <button className="register-secondary-button" onClick={closeCampaignModal} type="button">
                  Cancel
                </button>
                <button className="register-submit register-submit-inline" disabled={saving} type="submit">
                  {saving ? 'Saving...' : editingCampaignId ? 'Save campaign' : 'Create campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </AdminShell>
  );
};

export default AdminDriverCampaignsPage;
